import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { HttpNotFoundError } from '~/api/errors';
import { useDownloadInvoiceFcFiles, useDownloadInvoiceFcXlsx, useInvoices } from '~/api/invoices';
import { QueryKeys } from '~/api/keys';
import { Button, ErrorState, useTable } from '~/components';
import { ColumnIds } from '~/constants/table';
import { isApproved } from '~/features/documents';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { toast } from '~/utils/toast';

import { InvoicesEmptyState } from './InvoicesEmptyState';
import { useInvoicesQueryMetadata, useInvoicesTableColumns, usePrefetchNextPage } from './InvoicesTable.hooks';
import {
  useInvoicesTableColumnFilters,
  useInvoicesTableGlobalFilter,
  useInvoicesTablePaginationState,
  useInvoicesTableSorting,
} from './InvoicesTable.nuqs';

type Props = {
  clientId?: number | null;
  invoiceTemplateId?: number | null;
};

export const InvoicesTable = ({ clientId = null, invoiceTemplateId = null }: Props) => {
  const [pagination, setPagination] = useInvoicesTablePaginationState();
  const [sorting, setSorting] = useInvoicesTableSorting();
  const [globalFilter, setGlobalFilter] = useInvoicesTableGlobalFilter();
  const [columnFilters, setColumnFilters] = useInvoicesTableColumnFilters();

  const isPinned = clientId !== null || invoiceTemplateId !== null;

  const queryMetadata = useInvoicesQueryMetadata({ clientId, invoiceTemplateId });

  const { data, isError, isPending, isPlaceholderData } = useInvoices(queryMetadata);
  const queryClient = useQueryClient();

  usePrefetchNextPage(data?.pagination.nextPage ?? null, queryMetadata);

  const downloadXlsxMutation = useDownloadInvoiceFcXlsx();
  const downloadFcFilesMutation = useDownloadInvoiceFcFiles();

  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'filters', 'invoices']);

  const columns = useInvoicesTableColumns(clientId !== null);
  const [table, Table] = useTable({
    data: data?.data ?? [],
    columns,
    initialState: {
      columnVisibility: {
        [ColumnIds.selector]: !isPinned,
        [ColumnIds.invoicesBrand]: false,
        [ColumnIds.invoicesClient]: clientId === null,
        [ColumnIds.invoicesDocYear]: false,
        [ColumnIds.invoicesDocQuarter]: false,
      },
    },
    state: { pagination, sorting, globalFilter, columnFilters },
    enableRowSelection: isPinned ? false : (row) => isApproved(row.original),
    enableSortingRemoval: false,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onGlobalFilterChange: (updater) => {
      setGlobalFilter(updater);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    onColumnFiltersChange: (updater) => {
      setColumnFilters(updater);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    rowCount: data?.pagination.totalResults,
  });

  if (isError) {
    return (
      <ErrorState
        onRetry={() => {
          queryClient.resetQueries({ queryKey: QueryKeys.invoices.index.metadata(queryMetadata) });
          setPagination(null);
          setSorting(null);
          setGlobalFilter(null);
          setColumnFilters(null);
        }}
        title={t('invoices:overview.error.title')}
      />
    );
  }

  const { filters } = queryMetadata;
  const hasActiveFilters =
    filters.search !== '' ||
    filters.clientId !== null ||
    filters.brandId !== null ||
    !!filters.year?.length ||
    !!filters.quarter?.length ||
    !!filters.status?.length;

  if (!isPinned && !hasActiveFilters && !isPlaceholderData && data?.pagination.totalResults === 0) {
    return <InvoicesEmptyState />;
  }

  return (
    <Table.Root table={table}>
      <Table.Menu>
        <Table.Search />

        {!isPinned && (
          <Table.Filters>
            <Table.Filter columnId={ColumnIds.invoicesClient} type="combobox" />
            <Table.Filter columnId={ColumnIds.invoicesBrand} hideWhen="singleOption" type="select" />
            <Table.Filter columnId={ColumnIds.invoicesDocYear} type="multiselect" />
            <Table.Filter columnId={ColumnIds.invoicesDocQuarter} type="multiselect" />
            <Table.Filter columnId={ColumnIds.invoicesStatus} type="multiselect" />
          </Table.Filters>
        )}
      </Table.Menu>

      <Table.Grid isLoading={isPending || isPlaceholderData}>
        <Table.Header />
        <Table.Body>
          {(row) => (
            <Table.Row
              actionLabel={
                row.original.status === 'Draft' ? t('invoices:overview.rowActions.edit.label') : t('invoices:overview.rowActions.details')
              }
              onClick={({ id, status }) =>
                status === 'Draft'
                  ? navigate(r(routes.createInvoice, { invoiceId: id }))
                  : navigate(r(routes.showInvoice, { invoiceId: id }))
              }
              row={row}
            />
          )}
        </Table.Body>
      </Table.Grid>

      <Table.Pagination showPageSizeOptions />

      {!isPinned && (
        <Table.Selection>
          {(selectedRowIds) => (
            <>
              <Button
                loading={downloadXlsxMutation.isPending}
                onClick={() =>
                  downloadXlsxMutation.mutate(
                    { ids: selectedRowIds },
                    {
                      onError: () => toast.danger({ description: t('common:error') }),
                    },
                  )
                }
              >
                {`${t('common:export')} (${selectedRowIds.length})`}
              </Button>

              <Button
                loading={downloadFcFilesMutation.isPending}
                onClick={() =>
                  downloadFcFilesMutation.mutate(
                    { ids: selectedRowIds },
                    {
                      onError: (error, { ids }) =>
                        toast.danger({
                          description:
                            error instanceof HttpNotFoundError
                              ? t('invoices:alerts.missingFcFiles', { count: ids.length })
                              : t('common:error'),
                        }),
                    },
                  )
                }
              >
                {`${t('common:datatable.download')} (${selectedRowIds.length})`}
              </Button>
            </>
          )}
        </Table.Selection>
      )}
    </Table.Root>
  );
};
